import * as React from 'react';
import { Linked, useModel } from '@type-r/react';
import { CompactSelect, Option, CompactTextArea, SelectFile } from 'lqx-react';
import { Events } from '@type-r/models';
import { SocDialog } from './socDialog';
import { InvoiceDialogState, DisputeReason, execDisputeActionBatch } from 'api/dispute';
import { SocDialogType } from '.';
import { SocCompactInvoices, useSocCommonStyles } from './socCommon';
import { useEffect } from 'react';


const filterDisputedUndisputed =
    (invoices: any[],
        hasCustomerApproverAccess: Boolean,
        hasCustomerWriteAccess: Boolean,
        hasSupplierApproverAccess: Boolean,
        hasSupplierWriteAccess: Boolean,
        hasSuperSupplierAccess: Boolean): { canNotBeDisputed: any[], canBeDisputed: any[] } => {
        return invoices.reduce((result, invoice) => {
            //Supplier Write and Supplier Approver users should be able to dispute or approve invoices which are Disputed by super supplier
            if ((invoice.initialActorUserType && invoice.initialActorUserType.toLowerCase() == 'supersupplier') || hasSuperSupplierAccess) {
                result.canBeDisputed.push(invoice);
                return result;
            }

            if (invoice.isEscalatedInternally) {
                if (invoice.disputeActiveSideRole.toLowerCase() == 'supplier') {
                    if (hasSupplierApproverAccess) {
                        //the invoice has been escalated by supplier write
                        if ((invoice.initialActorUserType && invoice.initialActorUserType.toLowerCase() == 'supplier')) {
                            result.canBeDisputed.push(invoice);
                            return result;
                        }
                    }
                    //EscalatedInternally by Supplier, both Customer user should not have access to Dispute
                    result.canNotBeDisputed.push(invoice);
                    return result;
                }

                if (hasCustomerWriteAccess) {
                    if (!hasCustomerApproverAccess) {
                        //CustomerWrite user should not have access to Dispute, only if he is not CustomerApprover
                        result.canNotBeDisputed.push(invoice);
                    } else {
                        //CustomerApprover should have access to dispute, even if he is also and CustomerWrite
                        result.canBeDisputed.push(invoice);
                    }
                } else if (hasCustomerApproverAccess) {
                    //CustomerApprover user should have access to Dispute it
                    result.canBeDisputed.push(invoice);
                }
                return result;
            }

            const invoiceStatus = invoice.invoiceStatus.toLowerCase();
            const disputeStatus = invoice.disputeStatus.toLowerCase();

            if (hasCustomerWriteAccess || hasCustomerApproverAccess) {
                if (invoiceStatus == 'approvalneeded' || invoiceStatus == 'awaitingremittance' ||
                    invoiceStatus == 'paymentintransit' || invoiceStatus == 'reconciled' ||
                    disputeStatus == 'disputedwithcustomer') {
                    result.canBeDisputed.push(invoice);
                    return result;
                } else {
                    result.canNotBeDisputed.push(invoice);
                    return result;
                }
            }

            if (hasSupplierWriteAccess || hasSupplierApproverAccess) {
                if (invoiceStatus == 'awaitingremittance' || invoiceStatus == 'paymentintransit' ||
                    invoiceStatus == 'reconciled' || disputeStatus == 'disputedwithsupplier') {
                    result.canBeDisputed.push(invoice);
                    return result;
                } else {
                    result.canNotBeDisputed.push(invoice);
                    return result;
                }
            }
        }, {
            canNotBeDisputed: [],
            canBeDisputed: []
        });
    }

export const DisputeDialog = (props: {
    $isOpen: Linked<boolean>,
    invoices: any,
    dialogType: SocDialogType,
    hasCustomerApproverAccess: Boolean,
    hasCustomerWriteAccess: Boolean,
    hasSupplierApproverAccess: Boolean,
    hasSupplierWriteAccess: Boolean,
    hasSuperSupplierAccess: Boolean
}) => {
    const filteredInvoices = filterDisputedUndisputed(props.invoices.models,
        props.hasCustomerApproverAccess,
        props.hasCustomerWriteAccess,
        props.hasSupplierApproverAccess,
        props.hasSupplierWriteAccess,
        props.hasSuperSupplierAccess);
    const state = useModel(InvoiceDialogState);
    let styles = useSocCommonStyles();

    useEffect(() => {
        state.onBehalfOf = 'customer';
    }, []);

    const addDispute = async () => {
        const requestData = [];
        let invoices = filteredInvoices.canBeDisputed;

        for (let i = 0; i < invoices.length; i++) {
            const invoice = invoices[i];
            if (state.file) {
                const attachmentId = await invoice.doUpload(state.file, state.comment, null, null);
                if (attachmentId) {
                    state.attachmentId = attachmentId;
                }
            }

            requestData.push(getTransformedInvoice(invoice, state));
        }

        const response = await execDisputeActionBatch(props.dialogType, requestData);

        throwErrorIfResponseHasErrors(response, invoices);
    }

    const throwErrorIfResponseHasErrors = (response: any, invoices: any[]) => {
        if (!response || !response.length) return;
        let errors = response.reduce((error, resp) =>
            resp.hasErrors
                ? error += `Invoice ${getInvoiceNumber(resp.invoiceId, invoices)} could not be processed because of the following error: ${resp.errorMessage}. `
                : error
            , '');
        if (!errors) return;
        throw new Error(errors);
    }

    const getInvoiceNumber = (invoiceId: any, invoices: any[]) => {
        if (!invoices || !invoices.length) return '';
        let invoice = invoices.find(x => x.id == invoiceId);
        return invoice ? invoice.invoiceNumber : '';
    }

    const getTransformedInvoice = (invoice: any, state: any) => {

        const transformedInvoice: any = {};

        transformedInvoice.invoiceId = invoice.id;
        transformedInvoice.emailTitle = state.attributes.emailTitle;
        transformedInvoice.toEmailList = state.attributes.toEmailList;
        transformedInvoice.fromEmail = state.attributes.fromEmail;
        transformedInvoice.comment = state.attributes.comment;
        transformedInvoice.attachmentId = state.attributes.attachmentId;
        transformedInvoice.reason = state.attributes.reason;
        transformedInvoice.issueCreditNote = state.attributes.issueCreditNote;
        transformedInvoice.change = state.attributes.change;
        transformedInvoice.disputeTransaction = state.attributes.disputeTransaction;
        transformedInvoice.onBehalfOf = state.attributes.onBehalfOf

        return transformedInvoice
    }

    const dialogType = props.dialogType;
    const isInApproveState = props.dialogType == 'approve';
    const hasSuperSupplierAccess = props.hasSuperSupplierAccess;
    return <SocDialog
        {...props}
        dialogType={dialogType}
        doIO={addDispute}
        onSuccess={() => Events.trigger("Soc:disputeAdded")}
        isValid={() => (state.$.reason.value.trim() != '' || isInApproveState) && !!filteredInvoices.canBeDisputed.length}
        child={
            <>
                {
                    !isInApproveState ?
                        <>
                            <CompactSelect required label="Reason" $value={state.$.reason} >
                                {Object.keys(DisputeReason)
                                    .filter(x => x !== DisputeReason.Changed)
                                    .map((r, i) =>
                                        <Option key={i} value={r}>{DisputeReason[r]}</Option>
                                    )}
                            </CompactSelect>
                            {
                                hasSuperSupplierAccess ?
                                    <>
                                        <CompactSelect label='On behalf of' $value={state.$.onBehalfOf}>
                                            <Option key='customer' value='customer'>Customer</Option>
                                            <Option key='supplier' value='supplier'>Supplier</Option>
                                        </CompactSelect>
                                    </>
                                    : <></>
                            }
                        </>
                        : <></>

                }
                <CompactTextArea label="Comments"
                    $value={state.$.comment}
                />
                <SelectFile label="Attachment"
                    accept=".csv,.edi,.xml,.txt,.json,.pdf,.xls,.doc,.docx,.xlsx,.png,.jpeg"
                    $value={state.$.file}
                />
                {
                    filteredInvoices.canBeDisputed.length ?
                        <SocCompactInvoices title={'Invoices will be disputed'} invoices={filteredInvoices.canBeDisputed}></SocCompactInvoices>
                        : <>
                            <br />
                            <p className={styles.missingUndisputed}>No valid invoices selected</p>
                        </>
                }
                {
                    filteredInvoices.canNotBeDisputed.length ?
                        <SocCompactInvoices title={'Below invoices are in Dispute state, hence updates are not being made to the invoices.'} invoices={filteredInvoices.canNotBeDisputed}></SocCompactInvoices>
                        : <></>
                }
            </>
        }
    />
}
